import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEye, FiEdit2, FiArchive, FiPlusCircle, FiPlay, FiCpu, FiBook, FiZap, FiCheckSquare, FiTag, FiShare2 } = FiIcons;

const formatIcon = { Video: FiPlay, Interactive: FiCpu, Course: FiBook, 'Micro-learning': FiZap, Quiz: FiCheckSquare };
const diffColor = {
  Beginner: 'bg-green-100 text-green-700',
  Intermediate: 'bg-yellow-100 text-yellow-700',
  Advanced: 'bg-red-100 text-red-700',
};
const statusColor = {
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Draft: 'bg-amber-100 text-amber-700 border-amber-200',
  Archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

const ModuleTable = ({ modules, onPreview, onEdit, onArchive, onAssign, onShare }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <th className="p-4">Module</th>
            <th className="p-4">Category</th>
            <th className="p-4">Format</th>
            <th className="p-4">Difficulty</th>
            <th className="p-4">Duration</th>
            <th className="p-4">Compliance</th>
            <th className="p-4">Uses</th>
            <th className="p-4">Status</th>
            <th className="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-sm">
          {modules.map(m => {
            const Icon = formatIcon[m.deliveryFormat] || FiBook;
            return (
              <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={Icon} className="text-blue-600 text-sm" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 leading-snug">{m.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{m.linkedComplianceTopic}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-slate-600 text-xs">{m.category}</td>
                <td className="p-4">
                  <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{m.deliveryFormat}</span>
                </td>
                <td className="p-4">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${diffColor[m.difficultyLevel]}`}>{m.difficultyLevel}</span>
                </td>
                <td className="p-4 text-slate-600 text-xs">{m.duration}</td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {m.complianceTags.slice(0, 2).map(tag => (
                      <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tag}</span>
                    ))}
                    {m.complianceTags.length > 2 && <span className="text-xs text-slate-400">+{m.complianceTags.length - 2}</span>}
                  </div>
                </td>
                <td className="p-4 text-slate-600 text-sm font-medium">{m.usageCount}</td>
                <td className="p-4">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusColor[m.status]}`}>{m.status}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onPreview(m)} title="Preview" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon={FiEye} /></button>
                    <button onClick={() => onEdit(m)} title="Edit" className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => onShare(m)} title="Share" disabled={!m.id || m.status === 'Draft'} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><SafeIcon icon={FiShare2} /></button>
                    <button onClick={() => onAssign(m)} title="Assign" disabled={m.status !== 'Active'} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><SafeIcon icon={FiPlusCircle} /></button>
                    <button onClick={() => onArchive(m)} title="Archive" className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><SafeIcon icon={FiArchive} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 bg-slate-50">
      Showing {modules.length} modules
    </div>
  </div>
);

export default ModuleTable;